import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { Layers, FolderOpen } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useUIStore } from '@/core/stores/ui.store';
import { useBoards, BoardStatusesManager } from '@/features/boards/public';

export function StatusesSection() {
  const { t } = useTranslation('settings');
  const selectedProjectId = useUIStore((s) => s.selectedProjectId);
  const { boards, isLoading } = useBoards(selectedProjectId ?? '');
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);

  if (!selectedProjectId) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 py-16 text-center"
        data-testid="settings-statuses-no-project"
      >
        <FolderOpen className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm font-medium text-foreground">{t('statuses.noProjectTitle')}</p>
        <p className="text-sm text-muted-foreground">{t('statuses.noProjectDescription')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="settings-statuses-section">
        <div className="pb-6 space-y-1.5">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-7 w-28 rounded-full" />
        </div>
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  const sorted = [...boards].sort((a, b) => a.order - b.order);

  // Validate against current boards — handles project switches and deleted boards without useEffect
  const activeBoardId =
    selectedBoardId && sorted.some((b) => b.id === selectedBoardId)
      ? selectedBoardId
      : (sorted[0]?.id ?? null);

  if (sorted.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 py-16 text-center"
        data-testid="settings-statuses-no-boards"
      >
        <Layers className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm font-medium text-foreground">{t('statuses.noBoardsTitle')}</p>
        <p className="text-sm text-muted-foreground">{t('statuses.noBoardsDescription')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-statuses-section">
      <div className="pb-6">
        <h2 className="text-lg font-semibold text-foreground">{t('statuses.sectionTitle')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('statuses.sectionDescription')}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {sorted.map((board) => {
          const isActive = board.id === activeBoardId;
          return (
            <button
              key={board.id}
              type="button"
              onClick={() => setSelectedBoardId(board.id)}
              data-testid={`settings-statuses-board-pill-${board.id}`}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs transition-colors',
                isActive
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-foreground hover:border-primary/50',
              )}
            >
              {board.name}
            </button>
          );
        })}
      </div>

      {activeBoardId && <BoardStatusesManager boardId={activeBoardId} />}
    </div>
  );
}
